import React, { useEffect, useState, useRef } from 'react';
import { message } from '../Message';
import axios from 'axios';
import Spinner from '../Spinner';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { CircularProgress } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Studio from '../Studio';

const photoTags = [
  'portrait',
  'landscape',
  'street',
  'fashion',
  'wedding',
  'product',
  'food',
  'architecture',
  'travel',
  'nature',
  'lifestyle',
  'event',
  'sports',
  'documentary',
  'black and white',
  'studio',
  'urban',
  'night',
  'macro',
  'vintage',
  'professional',
  'corporate',
  'business',
  'casual',
  'creative',
  'formal',
  'acting',
  'modeling',
  'linkedin',
  'executive',
  'academic',
  'medical',
  'legal',
  'tech',
  'artistic',
  'studio',
  'outdoor',
  'natural light',
  'dramatic',
  'minimalist'
];

const PhotoStyles = ({ t, isCreating, firebase, onFetchUser, userData }) => {
  const scrollableRef = useRef(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openAddV2, setOpenAddV2] = useState(false);
  const [mode, setMode] = useState("");
  const [removeId, setRemoveId] = useState("");
  const [openCompare, setOpenCompare] = useState(false);
  const [compareImages, setCompareImages] = useState({});
  const [editData, setEditData] = useState(null); // Add state for edit data
  const fileInputRef = useRef(null); // Create a ref for the file input
  const [isUploading, setIsUploading] = useState(false);
  const [addData, setAddData] = useState(null); // Add state for edit data
  const [photoPackOptions, setPhotoPackOptions] = useState([]); // Add this new state
  const [openAddFromPhotos, setOpenAddFromPhotos] = useState(false);
  const [showCollections, setShowCollections] = useState(false);

  useEffect(() => {
    firebase.getPhotoPacksV2()
      .then((result) => {
        const packs = result.docs.map(doc => ({
          id: doc.id,
          title: doc.data().title,
          image: doc.data().image,
          type: doc.data().type
        }));
        setPhotoPackOptions(packs);
      })
      .catch((error) => {
        console.log('Error fetching photo packs:', error);
      });
  }, []);

  const columns = [
    { id: 'action', label: 'Actions', minWidth: 40 },
    { id: 'tags', label: 'Tags', minWidth: 30 },
    { id: 'packIds', label: 'Packs', minWidth: 50 },
    { id: 'title', label: 'Title', minWidth: 50 },
    { id: 'description', label: 'Description', minWidth: 50 },
    { id: 'image', label: 'Thumbnail', minWidth: 50 },
    {
      id: 'prompt',
      label: 'Prompt',
      minWidth: 50,
      render: (value) => (
        <div style={{
          maxWidth: '200px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {value}
        </div>
      )
    }, { id: 'gender', label: 'Gender', minWidth: 50 },
    { id: 'active', label: 'Active', minWidth: 50 },
    { id: 'createdAt', label: 'Created At', minWidth: 100 },
  ];

  const addPhotoStyleFields = [
    {
      id: 'packIds',
      label: 'Photo Packs',
      type: 'multiselect',
      options: photoPackOptions
    },
    {
      id: 'tags',
      label: 'Tags',
      type: 'multiselect',
      options: photoTags
    },
    { id: 'title', label: 'Title', type: 'text' },
    { id: 'description', label: 'Description', type: 'text' },
    { id: 'image', label: 'Thumbnail', type: 'image' },
    { id: 'prompt', label: 'Prompt', type: 'textarea' },
    { id: 'gender', label: 'Gender', type: 'select', options: ['male', 'female', 'other'] },
    { id: 'active', label: 'Active', type: 'boolean' }
  ];

  const handleEdit = async (itemToEdit) => {
    setEditData(itemToEdit); // Set the item data to edit
    setOpen(true); // Open the dialog
  };

  useEffect(() => {
    firebase.getPhotoStyles()
      .then((result) => {
        const jobsArray = result.docs
          .map((doc) => ({
            photoId: doc.id,
            ...doc.data(),
          }));

        const updatedData = jobsArray.map((item) => {
          const date = new Date(item.createdAt);
          const gmtOffset = 7; // GMT+7
          date.setHours(date.getHours() + gmtOffset);
          const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date.toISOString().substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

          const packTitles = item.packIds?.map(id =>
            photoPackOptions.find(pack => pack.id === id)?.title || id
          ).join(', ') || '';

          return {
            ...item,
            createdAt: formattedDate,
            tags: item.tags.join(', '),
            packIds: packTitles,
            action: (
              <>
                <Button
                  style={{ marginRight: "2px", marginBottom: "2px" }}
                  variant="outlined"
                  color="success"
                  onClick={() => handleEdit(item)}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleRemove(item.photoId)}
                >
                  Remove
                </Button>
              </>
            ),
            image: (
              <>
                <img src={item.image} style={{ width: "200px", cursor: "pointer" }} onClick={(e) => onOpenCompare(e, item)}></img>
              </>
            )
          };
        });

        setRows(updatedData);
      })
      .catch((error) => {
        console.log(error);
      });

    return () => {
    };
  }, [photoPackOptions]);

  const handleRemove = async (itemId) => {
    setRemoveId(itemId);
    setMode("disapprove");
    // setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setOpenAdd(false);
    setOpenAddV2(false);
    setMode("");
  };

  const handleOk = async () => {
    // Validate required fields
    if (!editData.title || !editData.description || !editData.prompt || !editData.image || !editData.gender || !editData.packIds || editData.packIds.length === 0) {
      message.error("Please fill in all required fields (title, description, prompt, image, gender, and at least one photo pack)");
      return;
    }

    try {
      // Get the original photo style to compare pack changes
      const originalDoc = await firebase.getPhotoStyle(editData.photoId);
      const originalData = originalDoc.data();
      const originalPackIds = originalData.packIds || [];
      const newPackIds = editData.packIds || [];

      // Find packs to remove and add
      const packsToRemove = originalPackIds.filter(id => !newPackIds.includes(id));
      const packsToAdd = newPackIds.filter(id => !originalPackIds.includes(id));

      // Update the photo style document
      const updatedData = {
        title: editData.title,
        description: editData.description,
        prompt: editData.prompt,
        image: editData.image,
        gender: editData.gender,
        tags: editData.tags || [],
        packIds: newPackIds,
        active: editData.active || false,
        updatedAt: new Date().toISOString()
      };
      await firebase.updatePhotoStyle(editData.photoId, updatedData);

      // Remove style from old packs
      for (const packId of packsToRemove) {
        const packDoc = await firebase.getPhotoPackV2(packId);
        const packData = packDoc.data();

        const updateData = {};
        if (editData.gender === 'male') {
          updateData.malePrompts = (packData.malePrompts || []).filter(id => id !== editData.photoId);
        } else if (editData.gender === 'female') {
          updateData.femalePrompts = (packData.femalePrompts || []).filter(id => id !== editData.photoId);
        }

        await firebase.updatePhotoPackV2(packId, updateData);
      }

      // Add style to new packs
      for (const packId of packsToAdd) {
        const packDoc = await firebase.getPhotoPackV2(packId);
        const packData = packDoc.data();

        const updateData = {};
        if (editData.gender === 'male') {
          updateData.malePrompts = [...(packData.malePrompts || []), editData.photoId];
        } else if (editData.gender === 'female') {
          updateData.femalePrompts = [...(packData.femalePrompts || []), editData.photoId];
        }

        await firebase.updatePhotoPackV2(packId, updateData);
      }

      // Refresh the data
      const result = await firebase.getPhotoStyles();
      const updatedRows = result.docs.map((doc) => {
        const item = {
          photoId: doc.id,
          ...doc.data()
        };

        const date = new Date(item.createdAt);
        const gmtOffset = 7;
        date.setHours(date.getHours() + gmtOffset);
        const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date.toISOString().substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

        const packTitles = item.packIds?.map(id =>
          photoPackOptions.find(pack => pack.id === id)?.title || id
        ).join(', ') || '';

        return {
          ...item,
          createdAt: formattedDate,
          packIds: packTitles,
          tags: item.tags.join(', '),
          action: (
            <Button
              style={{ marginRight: "2px", marginBottom: "2px" }}
              variant="outlined"
              color="success"
              onClick={() => handleEdit(item)}
            >
              Edit
            </Button>
          ),
          image: (
            <img
              src={item.image}
              style={{ width: "200px", cursor: "pointer" }}
              onClick={(e) => onOpenCompare(e, item)}
              alt={item.title}
            />
          )
        };
      });

      setRows(updatedRows);
      handleClose();
      message.success("Photo style updated successfully!");

    } catch (error) {
      console.error('Error updating data:', error);
      message.error("Failed to update photo style");
    }
  };

  const handleAddNewStyle = async () => {
    // Validate required fields
    if (!addData || !addData.title || !addData.description || !addData.prompt || !addData.image || !addData.gender || !addData.packIds || addData.packIds.length === 0) {
      message.error("Please fill in all required fields (title, description, prompt, image, gender, and at least one photo pack)");
      return;
    }

    try {
      // Prepare the new photo style data
      const newData = {
        title: addData.title,
        description: addData.description,
        prompt: addData.prompt,
        image: addData.image,
        gender: addData.gender,
        tags: addData.tags || [],
        packIds: addData.packIds || [],
        active: addData.active || false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Add the new photo style and get its reference
      const newStyleRef = await firebase.addNewPhotoStyle(newData);
      const newStyleId = newStyleRef.id;

      // Update each selected photo pack
      const packUpdatePromises = addData.packIds.map(async (packId) => {
        const packDoc = await firebase.getPhotoPackV2(packId);
        const packData = packDoc.data();

        const updateData = {};
        if (addData.gender === 'male') {
          updateData.malePrompts = [...(packData.malePrompts || []), newStyleId];
        } else if (addData.gender === 'female') {
          updateData.femalePrompts = [...(packData.femalePrompts || []), newStyleId];
        }

        return firebase.updatePhotoPackV2(packId, updateData);
      });

      await Promise.all(packUpdatePromises);

      // Refresh the photo styles list
      const result = await firebase.getPhotoStyles();
      const updatedRows = result.docs.map((doc) => {
        const item = {
          photoId: doc.id,
          ...doc.data()
        };

        const date = new Date(item.createdAt);
        const gmtOffset = 7;
        date.setHours(date.getHours() + gmtOffset);
        const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date.toISOString().substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

        return {
          ...item,
          createdAt: formattedDate,
          action: (
            <Button
              style={{ marginRight: "2px", marginBottom: "2px" }}
              variant="outlined"
              color="success"
              onClick={() => handleEdit(item)}
            >
              Edit
            </Button>
          ),
          image: (
            <img
              src={item.image}
              style={{ width: "200px", cursor: "pointer" }}
              onClick={(e) => onOpenCompare(e, item)}
              alt={item.title}
            />
          )
        };
      });

      setRows(updatedRows);
      setAddData(null);
      handleClose();
      message.success("Photo style added successfully!");

    } catch (error) {
      console.error('Error saving data:', error);
      message.error("Failed to add photo style");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const onOpenCompare = (e, item) => {
    console.log(item);
    e.preventDefault();
    setCompareImages({
      before: item.originalUrl,
      after: item.upscaleUrl
    });
    setOpenCompare(true);
  }

  const handleImageUpload = async (file) => {
    if (!file) return; // Ensure a file is selected

    try {
      setIsUploading(true);
      const downloadURL = await firebase.uploadAdminFile(file); // Use the uploadAdminFile method
      setEditData({ ...editData, image: downloadURL }); // Update editData with the new image URL
      setAddData({ ...addData, image: downloadURL }); // Update editData with the new image URL

      message.success("Image uploaded successfully!"); // Notify success
      setIsUploading(false);
    } catch (error) {
      message.error("Image upload failed!"); // Notify failure
      console.error(error);
      setIsUploading(false);
    }
  }

  const addNewPhotoPack = () => {
    setEditData(null);
    setOpenAdd(true);
  }

  const addFromPhotos = () => {
    setEditData(null);
    setMode("studio");
    setShowCollections(false);
  }

  const handleConfirmRemove = async () => {
    try {
      // Get the photo style data before removal
      const styleDoc = await firebase.getPhotoStyle(removeId);
      const styleData = styleDoc.data();

      // Remove the style from all associated packs
      const packUpdatePromises = styleData.packIds.map(async (packId) => {
        const packDoc = await firebase.getPhotoPackV2(packId);
        const packData = packDoc.data();

        const updateData = {};
        if (styleData.gender === 'male') {
          updateData.malePrompts = (packData.malePrompts || []).filter(id => id !== removeId);
        } else if (styleData.gender === 'female') {
          updateData.femalePrompts = (packData.femalePrompts || []).filter(id => id !== removeId);
        }

        return firebase.updatePhotoPackV2(packId, updateData);
      });

      await Promise.all(packUpdatePromises);

      // Remove the photo style document
      await firebase.deletePhotoStyle(removeId);

      // Update the UI
      setRows(rows.filter(row => row.photoId !== removeId));

      handleClose();
      message.success("Photo style removed successfully!");
    } catch (error) {
      console.error('Error removing photo style:', error);
      message.error("Failed to remove photo style");
    }
  };

  const fetchPhotoStyles = async () => {
    firebase.getPhotoStyles()
      .then((result) => {
        const jobsArray = result.docs
          .map((doc) => ({
            photoId: doc.id,
            ...doc.data(),
          }));

        const updatedData = jobsArray.map((item) => {
          const date = new Date(item.createdAt);
          const gmtOffset = 7; // GMT+7
          date.setHours(date.getHours() + gmtOffset);
          const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date.toISOString().substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

          const packTitles = item.packIds?.map(id =>
            photoPackOptions.find(pack => pack.id === id)?.title || id
          ).join(', ') || '';

          return {
            ...item,
            createdAt: formattedDate,
            tags: item.tags.join(', '),
            packIds: packTitles,
            action: (
              <>
                <Button
                  style={{ marginRight: "2px", marginBottom: "2px" }}
                  variant="outlined"
                  color="success"
                  onClick={() => handleEdit(item)}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleRemove(item.photoId)}
                >
                  Remove
                </Button>
              </>
            ),
            image: (
              <>
                <img src={item.image} style={{ width: "200px", cursor: "pointer" }} onClick={(e) => onOpenCompare(e, item)}></img>
              </>
            )
          };
        });

        setRows(updatedData);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleCloseStudio = () => {
    fetchPhotoStyles();
    handleClose();
  };

  const addFromCollection = () => {
    setEditData(null);
    setMode("studio");
    setShowCollections(true);
  }

  return (
    <>
      <div className="my-design" ref={scrollableRef}>
        <h3>Manage Photo Styles</h3>
        <p style={{ color: '#8c8c8c', fontSize: '14px', marginTop: "0", marginBottom: "1rem" }}>
          {"Only admin can do."}
        </p>
        <Button
          className="mb-2 mr-2"
          style={{ marginRight: "4px" }}
          variant="outlined"
          color="success"
          onClick={() => addNewPhotoPack()}
        >
          Add manually
        </Button>
        <Button
          className="mb-2 mr-2"
          style={{ marginRight: "4px" }}
          variant="outlined"
          color="success"
          onClick={() => addFromPhotos()}
        >
          Add automatically
        </Button>
        <Button
          className="mb-2 mr-2"
          style={{ marginRight: "4px" }}
          variant="outlined"
          color="success"
          onClick={() => addFromCollection()}
        >
          Add from collection
        </Button>
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                        {columns.map((column) => {
                          const value = row[column.id];
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.render
                                ? column.render(value)
                                : column.id === 'category'
                                  ? Array.isArray(value) ? value.join(', ') : value
                                  : column.id === 'showOnLanding' || column.id === 'isHot' || column.id === 'active'
                                    ? value ? "✅" : "❌"
                                    : column.format && typeof value === 'number'
                                      ? column.format(value)
                                      : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </div>
      <div>
        <Dialog open={openAdd} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>Add new photo style</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {addPhotoStyleFields.map((field) => (
                <div key={field.id} style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{field.label}:</label>
                  {field.type === 'multiselect' ? (
                    <FormControl fullWidth variant="outlined" margin="normal">
                      <InputLabel id={`${field.id}-label`}>{field.label}</InputLabel>
                      <Select
                        labelId={`${field.id}-label`}
                        multiple
                        value={addData?.[field.id] || []}
                        onChange={(e) => setAddData({ ...addData, [field.id]: e.target.value })}
                        renderValue={(selected) => {
                          if (field.id === 'packIds') {
                            // Find the titles for selected IDs and join them
                            const selectedTitles = selected.map(id =>
                              photoPackOptions.find(pack => pack.id === id)?.title || id
                            ).join(', ');
                            return selectedTitles;
                          }
                          return selected.join(', ');
                        }}
                      >
                        {(typeof field.options === 'function'
                          ? field.options(addData?.type || 'ai_photography')
                          : field.id === 'packIds'
                            ? photoPackOptions
                            : field.options
                        ).map((option) => (
                          <MenuItem
                            key={option.id || option}
                            value={option.id || option}
                            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                          >
                            <Checkbox
                              checked={(addData?.[field.id] || []).indexOf(option.id || option) > -1}
                            />
                            {field.id === 'packIds' && option.image && (
                              <img
                                src={option.image}
                                alt={option.title}
                                style={{ width: '100px', height: '70px', objectFit: 'cover' }}
                              />
                            )}
                            {option.title || option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : field.type === 'boolean' ? (
                    <TextField
                      select
                      value={addData?.[field.id] || false}
                      onChange={(e) => setAddData({ ...addData, [field.id]: e.target.value })}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    >
                      <MenuItem value={true}>✅</MenuItem>
                      <MenuItem value={false}>❌</MenuItem>
                    </TextField>
                  ) : field.type === 'select' ? (
                    <TextField
                      select
                      value={addData?.[field.id] || ''}
                      onChange={(e) => setAddData({ ...addData, [field.id]: e.target.value })}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    >
                      {field.options.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </TextField>
                  ) : field.type === 'textarea' ? (
                    <TextField
                      value={addData?.[field.id] || ''}
                      onChange={(e) => setAddData({ ...addData, [field.id]: e.target.value })}
                      fullWidth
                      multiline
                      rows={10}
                      variant="outlined"
                      margin="normal"
                    />
                  ) : field.type === 'image' ? (
                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                      {isUploading ? (
                        <CircularProgress size={12} color="inherit" style={{ marginRight: '6px' }} />
                      ) : (
                        addData?.[field.id] && (
                          <img src={addData[field.id]} style={{ width: "200px", cursor: "pointer" }} alt="Thumbnail" />
                        )
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          await handleImageUpload(file);
                        }}
                        style={{ marginTop: '10px', display: 'none' }}
                        ref={fileInputRef}
                      />
                      <Button
                        style={{ marginTop: '10px' }}
                        variant="outlined"
                        onClick={() => fileInputRef.current.click()}
                      >
                        {addData?.[field.id] ? "Change Thumbnail" : "Add Thumbnail"}
                      </Button>
                    </div>
                  ) : (
                    <TextField
                      value={addData?.[field.id] || ''}
                      onChange={(e) => setAddData({ ...addData, [field.id]: e.target.value })}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    />
                  )}
                </div>
              ))}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Button disabled={isUploading} onClick={handleAddNewStyle} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </div>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Photo Style</DialogTitle>
        <DialogContent>
          {editData && (
            <DialogContentText>
              <div style={{ marginBottom: '1rem' }}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel id="packIds-label">Photo Packs</InputLabel>
                  <Select
                    labelId="packIds-label"
                    multiple
                    value={editData.packIds || []}
                    onChange={(e) => setEditData({ ...editData, packIds: e.target.value })}
                    renderValue={(selected) => {
                      const selectedTitles = selected.map(id =>
                        photoPackOptions.find(pack => pack.id === id)?.title || id
                      ).join(', ');
                      return selectedTitles;
                    }}
                  >
                    {photoPackOptions.map((pack) => (
                      <MenuItem
                        key={pack.id}
                        value={pack.id}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                      >
                        <Checkbox checked={(editData.packIds || []).indexOf(pack.id) > -1} />
                        {pack.image && (
                          <img
                            src={pack.image}
                            alt={pack.title}
                            style={{ width: '100px', height: '70px', objectFit: 'cover' }}
                          />
                        )}
                        {pack.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel id="tags-label">Tags</InputLabel>
                  <Select
                    labelId="tags-label"
                    multiple
                    value={editData.tags || []}
                    onChange={(e) => setEditData({ ...editData, tags: e.target.value })}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {photoTags.map((tag) => (
                      <MenuItem key={tag} value={tag}>
                        <Checkbox checked={(editData.tags || []).indexOf(tag) > -1} />
                        {tag}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  margin="normal"
                  label="Title"
                  value={editData.title || ''}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Description"
                  multiline
                  rows={2}
                  value={editData.description || ''}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                />

                <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem' }}>Thumbnail:</label>
                  {isUploading ? (
                    <CircularProgress size={24} />
                  ) : (
                    editData.image && (
                      <img
                        src={editData.image}
                        alt="Thumbnail"
                        style={{ width: "200px", marginBottom: '0.5rem', display: 'block' }}
                      />
                    )
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      await handleImageUpload(file);
                    }}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => fileInputRef.current.click()}
                  >
                    {editData.image ? "Change Thumbnail" : "Add Thumbnail"}
                  </Button>
                </div>

                <TextField
                  fullWidth
                  margin="normal"
                  label="Prompt"
                  multiline
                  rows={4}
                  value={editData.prompt || ''}
                  onChange={(e) => setEditData({ ...editData, prompt: e.target.value })}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel id="gender-label">Gender</InputLabel>
                  <Select
                    labelId="gender-label"
                    value={editData.gender || ''}
                  // onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel id="active-label">Active Status</InputLabel>
                  <Select
                    labelId="active-label"
                    value={editData.active || false}
                    onChange={(e) => setEditData({ ...editData, active: e.target.value })}
                  >
                    <MenuItem value={true}>✅ Active</MenuItem>
                    <MenuItem value={false}>❌ Inactive</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleOk}
            color="primary"
            variant="contained"
            disabled={isUploading}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mode === "disapprove"} onClose={handleClose}>
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this photo style? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mode === "studio"}
        onClose={handleClose}
        fullWidth
        maxWidth="md" // Sets a maximum width for desktop
        PaperProps={{
          sx: {
            height: {
              xs: '100%', // Full height on mobile
              sm: '90vh'  // 90% viewport height on desktop
            },
            m: {
              xs: 0, // No margin on mobile
              sm: 2  // Some margin on desktop
            },
            borderRadius: {
              xs: 0, // No border radius on mobile
              sm: 1  // Slight border radius on desktop
            }
          }
        }}
      >
        <DialogTitle>Add from photos</DialogTitle>
        <DialogContent sx={{
          p: {
            xs: 2,
            sm: 10
          },
          overflow: 'auto'
        }}>
          <Studio
            userData={userData}
            firebase={firebase}
            t={t}
            darkMode={false}
            photoPackOptions={photoPackOptions}
            addPhotoStyleFields={addPhotoStyleFields}
            handleCloseStudio={handleCloseStudio}
            isShowCollections={showCollections}
          />
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button
            onClick={handleClose}
            color="primary"
            variant="contained"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {isSpinning && <Spinner />}
    </>
  );
};

export default PhotoStyles;
